import React, { FC, useEffect, useState, useCallback } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { FaExpandAlt } from "react-icons/fa";
import CategoriesService from "../../http/CategoriesService";
import Loading from "../Loading";

interface PropsModal {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    categoryId: number | string | null;
    refreshTable: () => void;
}

const CategoryModalEdite: FC<PropsModal> = (props) => {
    const [category, setCategory] = useState<any | null>(null);
    const [name, setName] = useState<string>("");

    // Уведомления
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [validated, setValidated] = useState(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);

    // Флаг необходимости обновить родительскую таблицу при закрытии
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

    const fetchCategory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await CategoriesService.query({
                options: {where: { id: props.categoryId }}
            });

            if (data && data.length > 0) {
                const categoryData = data[0];
                if (categoryData.action === "DELETE") {
                    setError('Категория была удалена');
                    setNeedsRefresh(true);
                }
                setCategory(categoryData);
                setName(categoryData.name);
            } else {
                setError("Категория не найдена");
                setNeedsRefresh(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.categoryId]);

    useEffect(() => {
        if (props.visible) {
            void fetchCategory();
        }
    }, [props.visible, fetchCategory]);

    const handleSubmitEdite = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const fields = {
                values: { name: name.trim() },
                options: { where: { id: category.id } }
            };

            const { data } = await CategoriesService.update(fields);
            if (data) {
                setSuccess("Категория изменена");
                setError(null);
                setNeedsRefresh(true);
            } else {
                setWarning("Категория не изменилась");
            }
            // Перезагружаем данные с сервера для актуальности
            await fetchCategory();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const handleHideModal = () => {
        if (needsRefresh) {
            props.refreshTable();
        }
        props.setVisible(false);
    };

    return (
        <Modal show={props.visible} size={"lg"} fullscreen={fullscreen ? true : "md-down"} scrollable={true} onHide={handleHideModal}>
            <div className={"modal-header"}>
                <h5 className={"mb-0"}>Редактирование категории</h5>
                <div className={"d-flex gap-3 ai-center"}>
                    <button
                        type={"button"}
                        className={"btn-link border-0 bg-transparent"}
                        onClick={() => setFullscreen(!fullscreen)}
                    >
                        <FaExpandAlt />
                    </button>
                    <button type={"button"} className={"btn-close"} onClick={handleHideModal}></button>
                </div>
            </div>

            {loading ? (
                <div style={{ height: "200px" }} className={"d-flex ai-center jc-center"}>
                    <Loading />
                </div>
            ) : (
                <>
                    {category ? (
                        <Form noValidate={true} validated={validated} onSubmit={handleSubmitEdite}>
                            <Modal.Body>
                                {success && <Alert variant={"success"} onClose={() => setSuccess(null)}>{success}</Alert>}
                                {warning && <Alert variant={"warning"} onClose={() => setWarning(null)}>{warning}</Alert>}
                                {error && <Alert variant={"danger"} onClose={() => setError(null)}>{error}</Alert>}

                                {category.action !== "DELETE" && (
                                    <Form.Group as={Row} className={"mb-3"} controlId={"name"}>
                                        <Form.Label column={true} sm={"2"}>
                                            Название <span className={"text-danger"}>*</span>
                                        </Form.Label>
                                        <Col sm={"10"}>
                                            <Form.Control
                                                type={"text"}
                                                autoComplete={"off"}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required={true}
                                                disabled={!!success}
                                            />
                                            <Form.Control.Feedback type={"invalid"}>
                                                Поле обязательно для заполнения
                                            </Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                {category.action !== "DELETE" && !success && (
                                    <Button variant={"warning"} type={"submit"}>Изменить</Button>
                                )}
                                <Button variant={"secondary"} onClick={handleHideModal}>
                                    {category.action === "DELETE" || success ? "Закрыть" : "Отмена"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    ) : (
                        <Modal.Body>
                            <Alert variant={"warning"}>{error || "Категория не найдена"}</Alert>
                        </Modal.Body>
                    )}
                </>
            )}
        </Modal>
    );
};

export default CategoryModalEdite;
